// University at Albany (SUNY): public college page (#2840), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UAlbany's Common Data Set 2025-2026, linked section by section from the
//    Institutional Research, Planning and Effectiveness page; the file is an
//    Excel workbook on UAlbany's SharePoint (UniversityAtAlbany_CDS_2025-26.xlsx),
//    so the .edu page that links it is cited. C1 applicants, admits and
//    enrollees for Fall 2025, C8 test policy, C21/C22 plans.
//    Quirk: the C1 residency table is left out. It has no international
//    column, and its out-of-state admits (2,549) equal its in-state enrollees
//    (2,549), which would make 2,549 of 2,664 out-of-state applicants
//    admitted; that looks like an entry error, so no residency rows are shown.
//    The G1 costs are 2025-2026 figures (the CDS checks the box saying
//    2026-2027 costs were not yet available), so they are not used.
//  - UAlbany Undergraduate Admissions, How to Apply as a First-Year Student
//    (Early Action and Regular Admission dates, the test-optional policy and
//    the free application periods). The page offers a "Free Application
//    2027" waiver and free application weeks in October 2026, so it is the
//    fall 2027 cycle.
//  - UAlbany Tuition and Fees for Undergraduate Students: the "Total for
//    Fall 2026 & Spring 2027 Semesters" table (tuition, annual fees, a Quad
//    standard double and the myUnlimited meal plan). PARTIAL budget: that
//    table has no books, transportation or personal costs. The Cost of
//    Attendance page lists those, but it names no academic year and its
//    tuition and fees differ from the dated 2026-2027 table, so it is not
//    used.
//  - College Scorecard, school id 196060 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UAlbany Common Data Set 2025-2026',
        url: 'https://www.albany.edu/common-data-set-2025-2026',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UAlbany Undergraduate Admissions, How to Apply as a First-Year Student',
        url: 'https://www.albany.edu/admissions/how-apply-first-year-student',
        read: '2026-10-01',
    },
    tuition: {
        label: 'UAlbany, Tuition and Fees for Undergraduate Students',
        url: 'https://www.albany.edu/cost-aid/tuition-fees/undergraduate-students',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?196060-University-at-Albany',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-at-albany-suny',
    kbId: 'university_at_albany_suny',
    name: 'University at Albany',
    shortName: 'UAlbany',
    aliases: ['UAlbany', 'SUNY Albany'],
    location: 'Albany, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UAlbany brand identity); decoration only.
    branding: {
        primary: '#46166B',
        secondary: '#EEB211',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_at_albany_suny_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 35664,
        admitted: 25123,
        enrolled: 2801,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UAlbany\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UAlbany has no early decision plan. Its Early Action is not binding and not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UAlbany with the Common Application or the SUNY Application. One program asks for more:',
        rows: [
            { school: 'Guaranteed Admission Program with Upstate Medical University', requirement: 'SAT or ACT scores are required from applicants to this program; for other first-year applicants they are optional.', source: 'cds' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Admission (priority)', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: 'By the end of December', binding: false },
            { plan: 'Regular Admission', deadline: 'February 1', decision: 'By the end of March', binding: false },
        ],
        note: 'Both are priority deadlines: completed applicants are promised a decision by the dates shown, Regular Admission decisions go out on a rolling basis, and late applicants are considered as space allows. If you ask UAlbany to use your SAT or ACT scores, no decision is made until the scores arrive. The FAFSA priority date is December 1 and the enrollment deposit is due May 1. UAlbany waives its application fee through October 19, 2026, and SUNY Free Application Weeks run October 19 to November 2, 2026.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UAlbany is test-optional for first-year applicants. On the Common Application or the SUNY Application you choose whether to be reviewed with or without SAT or ACT scores, and UAlbany strongly recommends applying without them unless you believe they show your ability better than your high school record. Home-schooled applicants must send SAT or ACT scores. Freshman merit scholarships are decided without test scores.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required but considered if submitted for Fall 2025 applicants, and notes that scores are required from home-schooled students and from applicants to the Guaranteed Admission Program with Upstate Medical University.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [7070, 29130] },
            { item: 'Annual fees', amounts: [3646, 3646] },
            { item: 'Housing (on campus, standard double)', amounts: [10605, 10605] },
            { item: 'Food (unlimited meal plan)', amounts: [6600, 6600] },
        ],
        totals: [27921, 49981],
        note: 'Tuition, fees, housing and a meal plan only, for a full-time student, fall 2026 and spring 2027. UAlbany\'s table leaves out books, transportation and personal costs, so the full cost of attendance is higher.',
        source: 'tuition',
        netPrice: {
            average: 18266,
            byIncome: [
                { income: '$0 to $30,000', amount: 12868 },
                { income: '$30,001 to $48,000', amount: 14111 },
                { income: '$48,001 to $75,000', amount: 18712 },
                { income: '$75,001 to $110,000', amount: 21948 },
                { income: 'Over $110,000', amount: 25804 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UAlbany\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
